import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  model,
  Output,
} from '@angular/core';
import { ButtonComponent } from '@app/components/button';
import { DialogComponent } from '@app/components/dialog';
import { GeneralFormComponent } from '@app/features/tenant-configuration/general/general-form/general-form.component';
import { environment } from '@environments/_current/environment';
import { EnterpriseTenant, ProvisioningTypes } from '@skyfit/core-data-library';

import { formatEmailRegex } from '@app/utils/format-email-regex/format-email-regex';
import { TenantValidator } from '@app/utils/validators/tenant-validator';
import { TranslatePipe } from '@ngx-translate/core';

const defaultTenantValues = {
  name: '',
  description: '',
  config: {
    slugs: {
      value: [],
    },
    emailDomains: {
      value: [],
    },
    provisioningType: {
      value: ProvisioningTypes.emailRegex,
    },
  },
  features: {},
  contracts: [],
  enterpriseId: environment.aaptivEnterpriseId,
};

@Component({
  selector: 'app-tenant-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogComponent,
    GeneralFormComponent,
    ButtonComponent,
    TranslatePipe,
  ],
  templateUrl: './tenant-dialog.component.html',
  styleUrls: ['./tenant-dialog.component.css'],
})
export class TenantDialogComponent {
  private readonly tenantValidator = new TenantValidator();
  isOpen = model(false);
  name = model('');
  slugs = model('');
  emails = model('');
  description = model('');
  provisioningType = model<ProvisioningTypes>(ProvisioningTypes.emailRegex);
  @Output() handleClose = new EventEmitter<void>();
  @Output() save = new EventEmitter<EnterpriseTenant>();

  private readonly tenant = defaultTenantValues as EnterpriseTenant;

  nameError = computed(
    () =>
      this.tenantValidator.validateName(this.name()).validationObj.error.name ??
      ''
  );
  descriptionError = computed(
    () =>
      this.tenantValidator.validateDescription(this.description()).validationObj
        .error.description ?? ''
  );
  slugsError = computed(
    () =>
      this.tenantValidator.validateSlugs(this.slugs()).validationObj.error
        .slugs ?? ''
  );
  emailsError = computed(() => {
    if (this.provisioningType() === ProvisioningTypes.emailRegex) {
      return (
        this.tenantValidator.validateEmails(this.emails(), true).validationObj
          .error.emails ?? ''
      );
    }
    return '';
  });

  isFormValid = computed(
    () =>
      !this.nameError() &&
      !this.descriptionError() &&
      !this.slugsError() &&
      !this.emailsError()
  );

  onSave(): void {
    if (!this.isFormValid()) return;
    const formattedEmailDomainString = this.emails()
      .split(',')
      .map((email) => email.trim());

    this.save.emit({
      ...this.tenant,
      name: this.name(),
      description: this.description(),
      provisioningType: this.provisioningType(),
      config: {
        slugs: {
          value: this.slugs()
            .split(',')
            .map((slug) => slug.trim()),
        },
        emailDomains: {
          value: formattedEmailDomainString,
        },
        emailRegex: {
          value: formatEmailRegex(formattedEmailDomainString),
        },
        provisioningType: {
          value: this.provisioningType(),
        },
      },
    });
  }
}
